import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SubscribersRepository } from 'src/app/core/repositories/subscribers/SubscribersRepository';
import { AddSubscriberUserRequest } from 'src/app/models/subscribers/add-subscriber-user-request.model';
import {
  AddSubscriberErrorResponse,
  AddSubscriberUserResponse,
} from 'src/app/models/subscribers/add-subscriber-user-response.model';
import { CheckSubscriberResponse } from 'src/app/models/subscribers/check-subscriber-response.model';
import { NewSubscriberRequest } from 'src/app/models/subscribers/create-subscribers-request.model';
import { NewSubscriberResponse } from 'src/app/models/subscribers/create-subscribers-responses.model';
import { DeleteSubscriberResponse } from 'src/app/models/subscribers/delete-subscribers-response.model';
import { SubscribersResponse } from 'src/app/models/subscribers/get-subscribers-response.model';
import { UserSubscriberResponse } from 'src/app/models/subscribers/get-subscribers-user-response.model';
import { ParkingZoneResponse } from 'src/app/models/subscribers/parking-zone-response.model';
import { FolioAbonoRequest } from 'src/app/models/subscribers/payment/card-subscriber-folio-request.model';
import { CardSubscriberRequest } from 'src/app/models/subscribers/payment/card-subscriber-request.model';
import { CardSubscriberTransactionResponse } from 'src/app/models/subscribers/payment/card-subscriber-response.model';
import { SubscribersDetailsResponse } from 'src/app/models/subscribers/subscriber-details-response.model';
import { WalletSubscriberResponse } from 'src/app/models/subscribers/users/get-wallet-for-user-subscribers-response.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SubscribersRestRepository implements SubscribersRepository {
  private headers: HttpHeaders;

  constructor(private http: HttpClient) {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  // MARK: GET ALL SUBSCRIBERS
  getAllSubscribers(idSubscriber?: number): Observable<SubscribersResponse> {
    const params: any = {};

    if (idSubscriber) {
      params.idSubscriber = idSubscriber.toString();
    }

    const httpOptions = {
      headers: this.headers,
      params: params,
    };

    const apiUrl = `${environment.base_url}/${environment.subscribers.base}`;

    return this.http.get<SubscribersResponse>(apiUrl, httpOptions).pipe(
      map((response) => {
        if (!response) {
          throw new Error('Respuesta vacía del servidor');
        }
        return response;
      }),
      catchError((error) => {
        const errorResponse: SubscribersResponse = {
          result: 'Error',
          response: [],
        };
        return throwError(errorResponse);
      })
    );
  }

  //MARK: CREATE SUBSCRIBER
  createSubscriber(
    newSubscriber: NewSubscriberRequest
  ): Observable<NewSubscriberResponse> {
    const httpOptions = {
      headers: this.headers,
    };

    return this.http
      .post<NewSubscriberResponse>(
        `${environment.base_url}/${environment.subscribers.base}`,
        newSubscriber,
        httpOptions
      )
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Respuesta vacía del servidor');
          }
          return response;
        }),
        catchError((error) => {
          const errorResponse: NewSubscriberResponse = {
            result: 'Error',
            message: error.message || 'Error desconocido al crear el abonado',
            responseSubscriber: null,
            messages: [error.message || 'Error desconocido'],
          };
          return throwError(errorResponse);
        })
      );
  }

  //MARK: CHECK ZONES WITH SUBSCRIBERS
  checkZoneSubscriber(
    idEnclosure: number,
    idSubscriber?: number
  ): Observable<ParkingZoneResponse> {
    let params = new HttpParams().set('idEnclosure', idEnclosure.toString());

    if (idSubscriber !== undefined) {
      params = params.set('idSubscriber', idSubscriber.toString());
    }

    const httpOptions = {
      headers: this.headers,
      params: params,
    };

    return this.http
      .get<ParkingZoneResponse>(
        `${environment.url_base}/${environment.subscribers.check_zone_subscriber}`,
        httpOptions
      )
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Respuesta vacía del servidor');
          }
          return response;
        }),
        catchError((error) => {
          const errorResponse: ParkingZoneResponse = {
            result: 'Error',
            response: [
              error.message || 'Error desconocido al verificar estado',
            ],
          };
          return of(errorResponse);
        })
      );
  }

  // MARK: UPDATE SUBSCRIBER
  updateSubscriber(
    subscriberId: number,
    updatedSubscriber: NewSubscriberRequest
  ): Observable<NewSubscriberResponse> {
    const httpOptions = {
      headers: this.headers,
    };

    return this.http
      .put<NewSubscriberResponse>(
        `${environment.base_url}/${environment.subscribers.base}/${subscriberId}`,
        updatedSubscriber,
        httpOptions
      )
      .pipe(
        tap((response) => console.log('Respuesta del PUT:', response)),
        catchError((error) => {
          console.error('Error en PUT:', error);
          const errorResponse: NewSubscriberResponse = {
            result: 'Error',
            message: error.message,
            responseSubscriber: null,
            messages: [error.message],
          };
          return throwError(errorResponse);
        })
      );
  }

  // MARK: DELETE SUBSCRIBER
  deleteSubscriber(subscriberId: number): Observable<DeleteSubscriberResponse> {
    const httpOptions = {
      headers: this.headers,
    };

    return this.http
      .put<DeleteSubscriberResponse>(
        `${environment.base_url}/${environment.subscribers.base}/${environment.subscribers.delete_subscriber}${subscriberId}`,
        httpOptions
      )
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Empty response from server');
          }
          return response;
        }),
        catchError((error) => {
          const errorResponse: DeleteSubscriberResponse = {
            result: 'Error',
            response: [error.message || 'Unknown error deleting subscriber'],
          };
          return throwError(errorResponse);
        })
      );
  }

  // MARK: GET ALL SUBSCRIBERS
  getUserSubscribers(idEnclosure: number): Observable<UserSubscriberResponse> {
    const apiUrl = `${environment.base_url}/${environment.subscribers.base}/${environment.subscribers.get_subscribers}/${idEnclosure}`;

    return this.http
      .get<UserSubscriberResponse>(apiUrl, { headers: this.headers })
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Respuesta vacía del servidor');
          }
          return response;
        }),
        catchError((error) => {
          const errorResponse: UserSubscriberResponse = {
            result: 'Error',
            response: [],
          };
          return throwError(errorResponse);
        })
      );
  }

  // MARK: GET ALL SUBSCRIBERS
  getSubscriberDetails(
    idAbono: number
  ): Observable<SubscribersDetailsResponse> {
    const apiUrl = `${environment.base_url}/${environment.subscribers.base}/${idAbono}`;

    return this.http
      .get<SubscribersDetailsResponse>(apiUrl, { headers: this.headers })
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Respuesta vacía del servidor');
          }
          return response;
        }),
        catchError((error) => {
          const errorResponse: SubscribersDetailsResponse = {
            result: 'Error',
            response: [],
          };
          return throwError(errorResponse);
        })
      );
  }

  // MARK: ADD SUBSCRIBER USER
  addSubscriberUser(
    subscriberUserData: AddSubscriberUserRequest
  ): Observable<AddSubscriberUserResponse> {
    const httpOptions = {
      headers: this.headers,
      observe: 'response' as const, // Esto nos permite acceder a la respuesta completa
    };

    return this.http
      .post<AddSubscriberUserResponse>(
        `${environment.base_url}/${environment.subscribers.add_subscriber_user}`,
        subscriberUserData,
        httpOptions
      )
      .pipe(
        map((httpResponse) => {
          // Extraemos el cuerpo de la respuesta
          const response = httpResponse.body;
          if (!response) {
            throw new Error('Empty response from server');
          }
          return response;
        }),
        catchError((error) => {
          // Si el error tiene una respuesta con cuerpo, lo devolvemos como un next
          if (
            error.error &&
            (error.error.result === 'ok' || error.error.result === 'error')
          ) {
            return of(error.error as AddSubscriberUserResponse);
          }

          // Para otros tipos de errores, creamos una respuesta de error genérica
          const errorResponse: AddSubscriberErrorResponse = {
            result: 'error',
            message:
              error.message ||
              'Error desconocido al agregar abonado al usuario',
          };
          return of(errorResponse);
        })
      );
  }

  // MARK: FIRST PHASE PAYMENT FOR SUBSCRIBER
  paySubscriberById(
    idAbonoUsuario: number,
    request: CardSubscriberRequest
  ): Observable<CardSubscriberTransactionResponse> {
    const httpOptions = {
      headers: this.headers,
    };

    const endpoint = `${environment.url_base}/${environment.subscribers.pay_subscribe_first_phase}/${idAbonoUsuario}`;

    return this.http
      .post<CardSubscriberTransactionResponse>(endpoint, request, httpOptions)
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Empty response from server');
          }
          return response;
        }),
        catchError((error) => {
          const errorResponse: CardSubscriberTransactionResponse = {
            message: error.message || 'Error during payment processing',
            data: {
              type: '',
              clientRequestId: '',
              apiTraceId: '',
              ipgTransactionId: '',
              orderId: '',
              transactionType: '',
              paymentToken: {
                reusable: false,
                declineDuplicates: false,
                brand: '',
                type: '',
              },
              transactionOrigin: '',
              paymentMethodDetails: {
                paymentCard: {
                  expiryDate: {
                    month: '',
                    year: '',
                  },
                  bin: '',
                  last4: '',
                  brand: '',
                },
                paymentMethodType: '',
                paymentMethodBrand: '',
              },
              country: '',
              transactionTime: 0,
              transactionAmount: {
                total: 0,
                currency: '',
                components: {
                  subtotal: 0,
                },
              },
              transactionStatus: 'ERROR',
              approvalCode: '',
              authenticationResponse: {
                type: '',
                version: '',
                secure3dMethod: {
                  methodForm: '',
                  secure3dTransId: '',
                },
              },
              error: true,
              declined: true,
              associationResponseCode: '',
              total: 0,
              last4: '',
            },
          };
          return throwError(errorResponse);
        })
      );
  }

  // MARK: SECOND PHASE PAYMENT CALLBACK FOR SUBSCRIBER (NEW METHOD)
  processSubscriberPaymentCallback(
    callbackData: FolioAbonoRequest
  ): Observable<CardSubscriberTransactionResponse> {
    const httpOptions = {
      headers: this.headers,
    };

    const endpoint = `${environment.url_base}/${environment.subscribers.pay_subscribe_second_phase}`;

    return this.http
      .post<CardSubscriberTransactionResponse>(
        endpoint,
        callbackData,
        httpOptions
      )
      .pipe(
        map((response) => {
          if (!response) {
            if (!response || !response.data) {
              console.warn('Received response without data object:', response);
            }
            return response;
          }
          return response;
        }),
        catchError((error) => {
          console.error(
            'Error during subscriber payment callback processing:',
            error
          );
          const errorResponse: CardSubscriberTransactionResponse = {
            message:
              error.message || 'Error during payment callback processing',
            data: {
              type: '',
              clientRequestId: '',
              apiTraceId: '',
              ipgTransactionId: '',
              orderId: error.error?.data?.orderId || '',
              transactionType: '',
              paymentToken: {
                reusable: false,
                declineDuplicates: false,
                brand: '',
                type: '',
              },
              transactionOrigin: '',
              paymentMethodDetails: {
                paymentCard: {
                  expiryDate: { month: '', year: '' },
                  bin: '',
                  last4: '',
                  brand: '',
                },
                paymentMethodType: '',
                paymentMethodBrand: '',
              },
              country: '',
              transactionTime: 0,
              transactionAmount: {
                total: 0,
                currency: '',
                components: { subtotal: 0 },
              },
              transactionStatus: 'ERROR',
              approvalCode: '',
              authenticationResponse: {
                type: '',
                version: '',
                secure3dMethod: { methodForm: '', secure3dTransId: '' },
              },
              error: true,
              declined: true,
              associationResponseCode:
                error.error?.data?.associationResponseCode || '',
              total: 0,
              last4: '',
            },
          };
          return throwError(errorResponse);
        })
      );
  }

  //MARK: CHECK SUBSCRIBER AFTER PAY
  getSubscriberDataByIdAbono(
    idAbono: number
  ): Observable<CheckSubscriberResponse> {
    const apiUrl = `${environment.base_url}/${environment.subscribers.add_subscriber_user}/${idAbono}`;

    return this.http
      .get<CheckSubscriberResponse>(apiUrl, { headers: this.headers })
      .pipe(
        map((response) => {
          if (!response) {
            console.error(
              `Respuesta vacía del servidor para idAbono ${idAbono} en ${apiUrl}`
            );
            throw new Error('Respuesta vacía del servidor');
          }
          return response;
        }),
        catchError((error) => {
          console.error(
            `Error al obtener datos del abonado con idAbono ${idAbono} desde ${apiUrl}:`,
            error
          );
          const errorResponse: CheckSubscriberResponse = {
            result: 'Error',
            response: [],
          };
          return throwError(() => errorResponse);
        })
      );
  }

  //MARK: GET WALLET FOR USER SUBSCRIBERS
  getWalletSubscribers(idUser: number): Observable<WalletSubscriberResponse> {
    const httpOptions = {
      headers: this.headers,
    };

    const endpoint = `${environment.base_url}/${environment.subscribers.wallet_subscribers}${idUser}`;

    return this.http.get<WalletSubscriberResponse>(endpoint, httpOptions).pipe(
      map((response) => {
        if (!response) {
          throw new Error('Empty response from server');
        }
        return response;
      }),
      catchError((error) => {
        const errorResponse: WalletSubscriberResponse = {
          result: 'Error',
          response: [],
          QRs: [],
        };
        return throwError(errorResponse);
      })
    );
  }
}
